/**
 * 🔔 Business Hours Event Service
 * Real-time event broadcasting for business hours changes
 * 
 * Usage:
 *   import businessHoursEventService from '../services/businessHoursEventService';
 * 
 *   // Subscribe to events
 *   const unsubscribe = businessHoursEventService.subscribe((event) => {
 *     if (event.type === 'HOURS_UPDATED') console.log('Hours changed:', event.data);
 *   });
 * 
 *   // Broadcast events
 *   businessHoursEventService.hoursUpdated(hoursData);
 * 
 *   // Cleanup
 *   unsubscribe();
 */

class BusinessHoursEventService {
  constructor() {
    this.listeners = [];
  }

  /**
   * Subscribe to business hours events
   * @param {Function} callback - Function called when events occur
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.error('BusinessHoursEventService: callback must be a function');
      return () => {};
    }
    
    this.listeners.push(callback);
    
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Broadcast event to all subscribers
   * @param {Object} event - Event object with type and data
   */
  broadcast(event) {
    if (!event || !event.type) {
      console.error('BusinessHoursEventService: Invalid event format', event);
      return;
    }
    
    
    this.listeners.forEach((listener, index) => {
      try {
        listener(event);
      } catch (error) {
        console.error(`BusinessHoursEventService: Error in listener ${index}:`, error);
      }
    });
  }

  /**
   * Notify that business hours were updated
   * @param {Object} hours - The updated hours data
   */
  hoursUpdated(hours) {
    this.broadcast({
      type: 'HOURS_UPDATED',
      data: hours,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Notify that shift times should be refreshed
   */
  shiftsRefreshed() {
    this.broadcast({
      type: 'SHIFTS_REFRESHED',
      timestamp: new Date().toISOString()
    });
  }
}

// Export singleton instance
const businessHoursEventService = new BusinessHoursEventService();

export default businessHoursEventService;
